/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React from 'react';
import { Ducks, Role, User } from '@wit/vtv-x-foundation-lib';
import { Link, useHistory } from 'react-router-dom';
import { PrivateRoutePath, PublicRoutePath } from 'routes/paths';
import RouterUtils from 'services/utils/RouterUtils';
import translate from 'services/i18n/translate';
import { ProfileAvatar } from 'ui/components/';
import { useSelector } from 'react-redux';

interface Props {
  selectedProfileId: string;
  maxUsers: number;
  profiles: User[];
  handleSelectedProfile: (id: string) => void;
  handleSignOut: () => void;
  setDropdownVisible: (arg: boolean) => void;
}

export default function HeaderMainMenu({
  profiles,
  selectedProfileId,
  maxUsers,
  handleSelectedProfile,
  handleSignOut,
  setDropdownVisible,
}: Props): JSX.Element {
  const history = useHistory();

  // Consent and Notice GDPR Title
  const gdprData = useSelector(Ducks.Faqs.Selectors.getGdprData);
  const { notice, consent } = gdprData?.documents || {};
  const privacyTitle = () => {
    let title: string = translate['menu.privacy'];
    if (notice && !consent) {
      title = translate['menu.privacyNotice'];
    }
    if (consent && !notice) {
      title = translate['footer.about.privacyConsent'];
    }
    return title;
  };

  return (
    <div className="main-menu">
      <div className="profile-dropdown active">
        <ul className="menu-item-list thin-scroolbar">
          <li className="profileShade profile-title">
            <span>{translate['menu.profile.switchProfiles']}</span>
          </li>
          <ul className="thin-scroolbar profiles-list">
            {profiles.length === 0 && (
              <li className="profileShade error-message">
                <span>{translate.programInfoErrorProfiles}</span>
              </li>
            )}
            {profiles?.map((profile) => (
              <li
                id={`HeaderMainMenu_profileContainer___${profile.id}`}
                className={`profileShade ${selectedProfileId === profile.id ? 'activeprofile' : ''}`}
                key={profile.id}
              >
                <a
                  id={`HeaderMainMenu_profile___${profile.id}`}
                  onClick={() => handleSelectedProfile(profile.id)}
                  className={`profileButton ${selectedProfileId === profile.id ? 'isSelectedProfile' : ''}`}
                >
                  <ProfileAvatar
                    profileId={profile.id}
                    name={
                      profile.role === Role.DEFAULT ? translate['profiles.household.name'] : profile.firstName || ''
                    }
                  />
                  <span className="label">
                    {profile.role === Role.DEFAULT ? translate['profiles.household.name'] : profile.firstName || ''}
                  </span>
                </a>
                {profile.role !== Role.DEFAULT && selectedProfileId === profile.id && (
                  <a
                    id="HeaderMainMenu_editProfile"
                    className="editButton"
                    onClick={() => {
                      setDropdownVisible(false);
                      history.push(
                        RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.SETTINGS_PROFILES_EDIT, [profile.id])
                      );
                    }}
                  >
                    <span className="editButton-text">{translate['menu.profile.edit']}</span>
                  </a>
                )}
              </li>
            ))}
            {profiles.length < maxUsers && (
              <li className="profileShade">
                <a
                  id="HeaderMainMenu_addProfile"
                  className="profileButton"
                  onClick={() => {
                    history.push(PrivateRoutePath.SETTINGS_PROFILES_ADD);
                    setDropdownVisible(false);
                  }}
                >
                  <span className="avatar newProfile" />
                  <span className="label">{translate['profiles.addnewprofile']}</span>
                </a>
              </li>
            )}
          </ul>
          <li className="profileShade">
            <Link id="HeaderMainMenu_settings" to={PrivateRoutePath.SETTINGS} onClick={() => setDropdownVisible(false)}>
              <span>{translate['menu.profile.settings']}</span>
            </Link>
          </li>
          <li className="profileShade">
            <Link id="HeaderMainMenu_privacy" to={PublicRoutePath.PRIVACY} onClick={() => setDropdownVisible(false)}>
              <span>{privacyTitle()}</span>
            </Link>
          </li>
          <li className="profileShade">
            <Link id="HeaderMainMenu__helpAndInfo" to={PublicRoutePath.HELP} onClick={() => setDropdownVisible(false)}>
              <span>{translate['menu.profile.helpAndInfo']}</span>
            </Link>
          </li>
          <li className="profileShade">
            <a onClick={handleSignOut} id="HeaderMainMenu_signOut">
              <span>{translate['menu.profile.signOut']}</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
}
